import React, { Component, Fragment } from 'react';
import * as _ from 'lodash';
import * as PropTypes from 'prop-types';
import { ContentBox } from '../../../../shared/content-box/ContentBox';
import { RuleTypeLabelPipe } from '../../../../shared/pipes/RuleTypeLabelPipe';
import { EventTypeLabelPipe } from '../../../../shared/pipes/EventTypeLabelPipe';
import { getRuleCategoryFriendlyName } from '../../../../services/RuleCategory';
import TopRiskyPlayers from '../../dashboard/content/TopRiskyPlayers';
import { StatTile } from '../common/StatTile';
import { getScenarioName, isActiveStatus } from '../simulation.model';
import '../../../../shared/table/AlertsTable.scss';

/* The alerts of a rule, or of a scenario's expectation: "Too many deposits ×3" for each rule. */
const RuleCounts = ({ counts }) =>
    _.isEmpty(counts) ? (
        <span className={'sim-muted'}>None</span>
    ) : (
        <Fragment>
            {counts.map(({ ruleType, count }) => (
                <div key={ruleType}>
                    {RuleTypeLabelPipe(ruleType)} × {count}
                </div>
            ))}
        </Fragment>
    );

const Match = ({ match }) =>
    match === true ? (
        <span className={'sim-match yes'}>Matches</span>
    ) : match === false ? (
        <span className={'sim-match no'}>Differs</span>
    ) : (
        <span
            className={'sim-match pending'}
            title={
                'A scenario is only compared with the alerts it expects when the run has completed.'
            }
        >
            Not evaluated
        </span>
    );

/*
  What a run has produced: the events and players, the alerts by rule, the players at most risk and, for the scenarios, the alerts
  that were expected next to the ones that were raised. A run that is not over shows what it has produced so far.
*/
export class RunResults extends Component {
    static propTypes = {
        results: PropTypes.object,
        // The catalog of scenarios by id, for their names
        scenariosById: PropTypes.object
    };

    renderAlertsByRule(alerts) {
        const rows = _.orderBy(alerts.byRuleType, 'count', 'desc');

        return (
            <div className={'table sim-padded'}>
                <table>
                    <tbody>
                        <tr className="headers">
                            <th>Rule</th>
                            <th>Category</th>
                            <th className={'align-right'}>Alerts</th>
                            <th className={'align-right'}>Open</th>
                            <th className={'align-right'}>Low</th>
                            <th className={'align-right'}>Medium</th>
                            <th className={'align-right'}>High</th>
                        </tr>
                        {_.isEmpty(rows) ? (
                            <tr className="data">
                                <td colSpan={7} className={'sim-muted'}>
                                    No alerts were raised.
                                </td>
                            </tr>
                        ) : (
                            rows.map(rule => (
                                <tr key={rule.ruleType} className="data">
                                    <td>{RuleTypeLabelPipe(rule.ruleType)}</td>
                                    <td>
                                        {getRuleCategoryFriendlyName(
                                            rule.category
                                        ) || rule.category}
                                    </td>
                                    <td className={'align-right'}>
                                        {rule.count}
                                    </td>
                                    <td className={'align-right'}>
                                        {rule.open}
                                    </td>
                                    <td className={'align-right'}>
                                        {_.get(rule, 'byLevel.LOW', 0)}
                                    </td>
                                    <td className={'align-right'}>
                                        {_.get(rule, 'byLevel.MEDIUM', 0)}
                                    </td>
                                    <td className={'align-right'}>
                                        {_.get(rule, 'byLevel.HIGH', 0)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    renderEvents(events) {
        const rows = _.orderBy(
            _.map(events.byType, (count, type) => ({ type, count })),
            'count',
            'desc'
        );

        return (
            <div className={'table sim-padded'}>
                <table>
                    <tbody>
                        <tr className="headers">
                            <th>Event</th>
                            <th className={'align-right'}>Count</th>
                        </tr>
                        {_.isEmpty(rows) ? (
                            <tr className="data">
                                <td colSpan={2} className={'sim-muted'}>
                                    No events were processed yet.
                                </td>
                            </tr>
                        ) : (
                            rows.map(({ type, count }) => (
                                <tr key={type} className="data">
                                    <td>{EventTypeLabelPipe(type)}</td>
                                    <td className={'align-right'}>{count}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    renderScenarios(scenarios) {
        const { scenariosById } = this.props;

        return (
            <div className={'table sim-padded'}>
                <table>
                    <tbody>
                        <tr className="headers">
                            <th>Scenario</th>
                            <th>Expected alerts</th>
                            <th>Raised alerts</th>
                            <th
                                title={
                                    'Alerts that may also be there, because the scenario cannot rule them out: they depend on what it does not control, such as the risk level computed for a player. They never count as a difference.'
                                }
                            >
                                Tolerated
                            </th>
                            <th>Outcome</th>
                        </tr>
                        {scenarios.map(scenario => (
                            <tr key={scenario.id} className="data">
                                <td>
                                    {getScenarioName(
                                        scenariosById,
                                        scenario.id
                                    )}
                                </td>
                                <td>
                                    <RuleCounts counts={scenario.expected} />
                                </td>
                                <td>
                                    <RuleCounts counts={scenario.actual} />
                                </td>
                                <td>
                                    <RuleCounts counts={scenario.tolerated} />
                                </td>
                                <td>
                                    <Match match={scenario.match} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { results } = this.props;

        if (!results) {
            return null;
        }
        const { events, players, alerts, scenarios, topRiskyPlayers } = results;

        return (
            <div className={'sim-results'}>
                {isActiveStatus(results.status) && (
                    <div className={'sim-banner info'}>
                        <i className={'material-icons'}>info</i>
                        <div>
                            These are the results so far. They are updated while
                            the run is active, and compared with the alerts each
                            scenario expects when it completes.
                        </div>
                    </div>
                )}

                <div className={'sim-stats'}>
                    <StatTile label={'Events'} value={events.total} />
                    <StatTile label={'Players'} value={players.created} />
                    <StatTile label={'Alerts'} value={alerts.total} />
                    <StatTile label={'Open alerts'} value={alerts.open} />
                </div>

                <div className={'sim-section'}>
                    <ContentBox
                        title={'Alerts by rule'}
                        content={this.renderAlertsByRule(alerts)}
                    />
                </div>
                {!_.isEmpty(scenarios) && (
                    <div className={'sim-section'}>
                        <ContentBox
                            title={'Expected and raised alerts'}
                            tooltipMessage={
                                'Each scenario states the alerts it should raise. A run is compared with them when it completes.'
                            }
                            content={this.renderScenarios(scenarios)}
                        />
                    </div>
                )}
                <div className={'sim-section'}>
                    <ContentBox
                        title={'Events'}
                        content={this.renderEvents(events)}
                    />
                </div>
                {!_.isEmpty(topRiskyPlayers) && (
                    <div className={'sim-section'}>
                        <ContentBox
                            title={'Players at most risk'}
                            content={
                                <TopRiskyPlayers
                                    riskyPlayers={topRiskyPlayers}
                                />
                            }
                        />
                    </div>
                )}
            </div>
        );
    }
}
